import React from 'react'
import styled from 'styled-components'
import useLocale from 'js/hooks/useLocale'
import NovoNordiskLogo from 'media/novo-nordisk-logo.svg?svgr-webpack'
import { trackGlp1CtaClick } from 'js/analytics'

type Props = {
  show: boolean
  url?: string
  buttonLabel?: string
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void
}

const FloatingContainer = styled.div<{ $show: boolean }>`
  position: fixed;
  pointer-events: auto;
  bottom: var(--spacing-lg);
  right: var(--spacing-lg);
  z-index: 1000;
  opacity: ${(props) => (props.$show ? 1 : 0)};
  visibility: ${(props) => (props.$show ? 'visible' : 'hidden')};
  transform: ${(props) => (props.$show ? 'translateY(0)' : 'translateY(20px)')};
  transition:
    opacity var(--transition-base),
    visibility var(--transition-base),
    transform var(--transition-base);
  pointer-events: ${(props) => (props.$show ? 'auto' : 'none')};

  @media (max-width: 767px) {
    bottom: var(--spacing-md);
    right: var(--spacing-md);
  }
`

const CtaButton = styled.a<{ $isSwedish: boolean }>`
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  background: ${({ $isSwedish }) =>
    $isSwedish ? '#002060' : 'var(--color-nn-dark-forest)'};
  border-radius: var(--border-radius-md);
  padding: ${({ $isSwedish }) => ($isSwedish ? '10px 16px' : '10px 16px')};
  text-decoration: none;
  box-shadow: var(--shadow-lg);
  transition:
    transform var(--transition-base),
    box-shadow var(--transition-base);
  max-width: 300px;

  &:hover,
  &:focus {
    transform: translateY(-2px);
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.2);
  }

  @media (max-width: 767px) {
    padding: ${({ $isSwedish }) =>
      $isSwedish
        ? 'var(--spacing-sm) 16px'
        : 'var(--spacing-sm) var(--spacing-md)'};
    gap: var(--spacing-sm);
    max-width: 320px;
  }
`

const LogoWrapper = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 40px;
    height: 28px;
    filter: brightness(0) invert(1); /* Makes logo white */
  }

  @media (max-width: 767px) {
    svg {
      width: 32px;
      height: 22px;
    }
  }
`

const TextWrapper = styled.div<{ $isSwedish: boolean }>`
  display: ${({ $isSwedish }) => ($isSwedish ? 'flex' : 'block')};
  flex-direction: ${({ $isSwedish }) => ($isSwedish ? 'column' : 'row')};
  align-items: ${({ $isSwedish }) => ($isSwedish ? 'flex-start' : 'center')};
  flex: 1;
  color: ${({ $isSwedish }) =>
    $isSwedish ? '#FFF' : 'var(--color-nn-accent)'};
  font-weight: 700;
  font-size: 14px;
  line-height: 1.2;
  text-transform: ${({ $isSwedish }) => ($isSwedish ? 'none' : 'uppercase')};
  letter-spacing: 0.02em;
`

const ButtonTextLine = styled.span<{
  $isSecondLine?: boolean
  $isFirstLine?: boolean
  $isLastLine?: boolean
  $isSwedish?: boolean
}>`
  ${({ $isFirstLine, $isSwedish }) =>
    $isFirstLine &&
    $isSwedish &&
    `
    font-size: 16px;
    font-weight: 700;
    line-height: 27px;
  `}
  ${({ $isFirstLine, $isSwedish }) =>
    $isFirstLine &&
    !$isSwedish &&
    `
    font-size: 14px;
    padding-right: 12px;
  `}
  ${({ $isSecondLine, $isSwedish }) =>
    $isSecondLine &&
    $isSwedish &&
    `
    font-size: 16px;
    font-weight: 450;
    line-height: 21px;
    margin-bottom: 17px;
  `}
  ${({ $isSecondLine, $isSwedish }) =>
    $isSecondLine &&
    !$isSwedish &&
    `
    font-size: 8px;
    display: block;
    margin-top: 2px;
  `}
  ${({ $isLastLine, $isSwedish }) =>
    $isLastLine &&
    $isSwedish &&
    `
    font-size: 10px;
    font-weight: 600;
    line-height: 28px;
    letter-spacing: 1.12px;
    text-transform: uppercase;
    border-top: 1px solid #fff;
  `}
`

const FloatingCta: React.FC<Props> = ({
  show,
  url = 'https://www.snakkomvekt.no?utm_source=LSNN',
  buttonLabel = 'Find a healthcare professional near you',
  onClick,
}) => {
  const { language } = useLocale()
  const isSwedish = language === 'sv'

  const renderButtonContent = () => {
    if (isSwedish && typeof buttonLabel === 'string') {
      const parts = buttonLabel.split('_').map((part) => part.trim())
      if (parts.length >= 3) {
        // Three parts: first line, second line, last line
        return (
          <TextWrapper $isSwedish={isSwedish}>
            <ButtonTextLine $isFirstLine $isSwedish={isSwedish}>
              {parts[0]}
            </ButtonTextLine>
            <ButtonTextLine $isSecondLine $isSwedish={isSwedish}>
              {parts[1]}
            </ButtonTextLine>
            <ButtonTextLine $isLastLine $isSwedish={isSwedish}>
              {parts.slice(2).join('_')}
            </ButtonTextLine>
          </TextWrapper>
        )
      } else if (parts.length >= 2) {
        // Fallback for two parts: first line, second line
        return (
          <TextWrapper $isSwedish={isSwedish}>
            <ButtonTextLine $isFirstLine $isSwedish={isSwedish}>
              {parts[0]}
            </ButtonTextLine>
            <ButtonTextLine $isSecondLine $isSwedish={isSwedish}>
              {parts.slice(1).join('_')}
            </ButtonTextLine>
          </TextWrapper>
        )
      }
    }
    return <TextWrapper $isSwedish={false}>{buttonLabel}</TextWrapper>
  }

  return (
    <FloatingContainer $show={show}>
      <CtaButton
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={buttonLabel}
        $isSwedish={isSwedish}
        onClick={
          onClick ? onClick : () => trackGlp1CtaClick({ source: 'floating' })
        }
      >
        {renderButtonContent()}
        {!isSwedish && (
          <LogoWrapper>
            <NovoNordiskLogo />
          </LogoWrapper>
        )}
      </CtaButton>
    </FloatingContainer>
  )
}

export default FloatingCta
